import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { X } from "lucide-react"
import { InfoCircle } from "iconsax-reactjs"

import { cn } from "@pma/utils/helpers"
import useTranslator from "@pma/hooks/use-translator"

const AUTO_DISMISS_MS = 4000

type ToastPayload = {
  message: string
  description?: string
  actionLabel?: string
  onAction?: () => void
}

type Listener = (payload: ToastPayload) => void
const listeners = new Set<Listener>()

export const showDeletePermissionToast = (payload: ToastPayload) => {
  listeners.forEach((l) => l(payload))
}

const DeletePermissionToast = () => {
  const { locale } = useTranslator()
  const isHebrew = locale === "he"
  const [toastData, setToastData] = useState<ToastPayload | null>(null)

  useEffect(() => {
    const listener: Listener = (payload) => setToastData(payload)
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])

  useEffect(() => {
    if (!toastData) return
    const id = window.setTimeout(() => setToastData(null), AUTO_DISMISS_MS)
    return () => window.clearTimeout(id)
  }, [toastData])

  if (!toastData) return null

  const hasDescription = !!toastData.description

  return createPortal(
    <div className='pointer-events-none fixed inset-x-0 bottom-24 z-[110] flex justify-center px-4 lg:bottom-8'>
      <div
        className={cn(
          "pointer-events-auto relative w-full max-w-[550px] rounded-[12px] border border-error-400 bg-error-50 px-4 py-4 shadow-lg",
          isHebrew ? "rtl" : "ltr",
        )}
      >
        {hasDescription ? (
          <>
            {/* Close button (top corner, only when description is present) */}
            <button
              type='button'
              className={cn(
                "absolute top-3 rounded-full p-1 text-error-500 transition-colors hover:bg-error-100",
                isHebrew ? "left-3" : "right-3",
              )}
              onClick={() => setToastData(null)}
            >
              <X className='h-4 w-4' />
            </button>

            {/* With description: stacked layout, action button on its own line */}
            <div className='flex flex-col gap-3'>
              <div className='flex items-start gap-2 pr-8'>
                <InfoCircle size={18} className='mt-[2px] shrink-0 text-error-500' />
                <div className='flex flex-col gap-1'>
                  <p className='text-sm font-semibold leading-5 text-error-600'>
                    {toastData.message}
                  </p>
                  <p className='text-sm leading-5 text-error-600'>{toastData.description}</p>
                </div>
              </div>

              {toastData.actionLabel && (
                <button
                  className='w-full rounded-full border border-error-400 bg-red-50 px-4 py-[2px] text-sm font-bold text-error-600 hover:bg-error-100'
                  onClick={() => {
                    toastData.onAction?.()
                    setToastData(null)
                  }}
                >
                  {toastData.actionLabel}
                </button>
              )}
            </div>
          </>
        ) : (
          // ── Without description: inline on sm+; stacked (button on new line) on small screens ──
          <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-3'>
            <div className='flex min-w-0 flex-1 items-center gap-2 pr-8 sm:pr-0'>
              <InfoCircle size={18} className='shrink-0 text-error-500' />
              <p className='text-sm leading-5 text-error-600'>{toastData.message}</p>
            </div>

            {toastData.actionLabel && (
              <button
                className='w-full shrink-0 rounded-full border border-error-400 bg-red-50 px-4 py-[2px] text-sm font-bold text-error-600 hover:bg-error-100 sm:w-auto'
                onClick={() => {
                  toastData.onAction?.()
                  setToastData(null)
                }}
              >
                {toastData.actionLabel}
              </button>
            )}

            <button
              type='button'
              className={cn(
                "absolute top-3 shrink-0 rounded-full p-1 text-error-600 transition-colors hover:bg-error-100 sm:static sm:top-auto",
                isHebrew ? "left-3 sm:left-auto" : "right-3 sm:right-auto",
              )}
              onClick={() => setToastData(null)}
            >
              <X className='h-3 w-3' />
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}

export default DeletePermissionToast
